import React, { useEffect, useRef } from 'react';
import { ChatMessage } from '../../models/clinicAgent';
import ProposalCard from './ProposalCard';
import ProposalResolutionCard from './ProposalResolutionCard';

interface MessageListProps {
  messages: ChatMessage[];
  typing: boolean;
  statusLabel: string | null;
  onConfirmProposal: (messageId: string, index: number) => void;
  onDiscardProposal: (messageId: string, index: number) => void;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  typing,
  statusLabel,
  onConfirmProposal,
  onDiscardProposal,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(messages.length);

  useEffect(() => {
    // Only auto-scroll when a new message arrives or the agent is working —
    // not when an existing message mutates (e.g. confirming/discarding a
    // proposal), so the clinician keeps focus on the proposal they're acting on.
    const grew = messages.length > prevCountRef.current;
    prevCountRef.current = messages.length;
    if (grew || typing || statusLabel) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, typing, statusLabel]);

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        background: '#f9fafb',
      }}
    >
      {messages.length === 0 && (
        <div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', marginTop: 24 }}>
          Pregúntame sobre tus pacientes, planes, recordatorios…
        </div>
      )}

      {messages.map((m) => {
        // Resolved-proposal events render as their own card, not a chat bubble.
        if (m.messageType === 'proposal_resolution' && m.resolution) {
          return <ProposalResolutionCard key={m.id} resolution={m.resolution} />;
        }

        // System notices (e.g. connection lost) render as a centered status line.
        if (m.messageType === 'system_notice') {
          return (
            <div
              key={m.id}
              style={{
                alignSelf: 'center',
                maxWidth: '90%',
                textAlign: 'center',
                fontSize: 12,
                fontStyle: 'italic',
                color: '#9ca3af',
                padding: '2px 8px',
              }}
            >
              {m.text}
            </div>
          );
        }

        const isUser = m.role === 'user';
        return (
          <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
            {m.imageUrl && (
              <img
                src={m.imageUrl}
                alt="Imagen adjunta"
                style={{
                  maxWidth: '85%',
                  maxHeight: 220,
                  borderRadius: 12,
                  border: '1px solid #e5e7eb',
                  marginBottom: m.text.trim() !== '' ? 6 : 0,
                  objectFit: 'cover',
                }}
              />
            )}
            {m.text.trim() !== '' && (
              <div
                style={{
                  maxWidth: '85%',
                  padding: '8px 12px',
                  borderRadius: 12,
                  fontSize: 14,
                  lineHeight: 1.45,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  background: isUser ? '#38b2ac' : '#fff',
                  color: isUser ? '#fff' : '#1f2937',
                  border: isUser ? 'none' : '1px solid #e5e7eb',
                }}
              >
                {m.text}
              </div>
            )}
            {m.proposals && m.proposals.length > 0 && (
              <div style={{ width: '100%' }}>
                {m.proposals.map((p, i) => (
                  <ProposalCard
                    key={`${m.id}-${i}`}
                    proposal={p}
                    onConfirm={() => onConfirmProposal(m.id, i)}
                    onDiscard={() => onDiscardProposal(m.id, i)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {(typing || statusLabel) && (
        <div style={{ alignSelf: 'flex-start' }}>
          <div
            style={{
              padding: '6px 12px',
              borderRadius: 12,
              background: '#fff',
              border: '1px solid #e5e7eb',
              color: '#6b7280',
              fontSize: 13,
              fontStyle: 'italic',
            }}
          >
            {statusLabel || 'Escribiendo…'}
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
};

export default MessageList;
